import getOktaConfig from "lib/getOktaConfig"

/**
 * Logs in via Okta session token and redirects through the Authorization Code flow.
 *
 * Standard path: redirects to our /api/auth/registration-callback, which handles
 * the token exchange and final redirect to the vendor's site.
 *
 * Vendor OAuth path (oauth=true): uses a site-specific Okta app (clientId resolved
 * server-side and passed as vendorOktaClientId) so the vendor's callback URL receives
 * the authorization code directly. Pardot is submitted client-side for all paths.
 *
 * Returns an error string on failure, null on success.
 */
export default async function oktaRegistrationRedirect({
    username,
    password,
    redirectUrl,
    pardotFormHandler,
    slug,
    preview = false,
    router,
    oauth = false,
    vendorOktaClientId = null,
}) {
    const response = await fetch("/api/okta/login", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            preview: preview,
        },
        body: JSON.stringify({ username, password }),
    }).then(res => res.json())

    if (!response.oktaSessionToken) {
        return "Invalid email or password. Please try again."
    }

    // Pardot fires client-side (fire-and-forget) for all paths
    if (pardotFormHandler) {
        fetch(pardotFormHandler, {
            method: "POST",
            headers: { "content-type": "application/x-www-form-urlencoded" },
            body: "email=" + encodeURIComponent(username),
        }).catch(err => console.error("Pardot submission failed:", err))
    }

    const oktaConfig = getOktaConfig(preview)

    if (oauth) {
        // Vendor OAuth path: vendor's Okta app receives the code at their callback URL
        const authorizeUrl =
            `${oktaConfig.issuer}/oauth2/default/v1/authorize` +
            `?client_id=${vendorOktaClientId}` +
            `&sessionToken=${response.oktaSessionToken}` +
            `&redirect_uri=${redirectUrl}` +
            `&response_type=code` +
            `&scope=openid profile email custom_profile` +
            `&prompt=none` +
            `&state=CRF`

        router.push(authorizeUrl)
        return null
    }

    // Standard path: our callback handles token exchange and final redirect
    const stateData = { redirectUrl, pardotFormHandler, origin: window.location.origin, slug, preview }
    const encodedState = btoa(JSON.stringify(stateData))

    const authorizeUrl =
        `${oktaConfig.issuer}/oauth2/default/v1/authorize` +
        `?client_id=${oktaConfig.regClientId}` +
        `&sessionToken=${response.oktaSessionToken}` +
        `&redirect_uri=${window.location.origin}/api/auth/registration-callback` +
        `&response_type=code` +
        `&scope=openid profile email custom_profile` +
        `&prompt=none` +
        `&state=${encodedState}`

    router.push(authorizeUrl)
    return null
}
